import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { ReportErrorButton } from "@/components/ReportError";
import {
  MessageSquareQuote, ShieldCheck, Clock3, ExternalLink, CalendarDays, Scale, CircleHelp,
} from "lucide-react";

/** Phase 10.4 — ASK THE CANDIDATES (public Q&A Corner).
 * Every invited candidate gets the identical question, limit, deadline and display
 * treatment. Non-responders are shown factually. Answers are never edited by WYV. */

export const VerificationBadge = ({ status }) => {
  if (status === "verified") {
    return (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px] gap-1" data-testid="badge-verified">
        <ShieldCheck className="h-3 w-3" /> Verified Candidate
      </Badge>
    );
  }
  if (status === "pending_verification") {
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px]" data-testid="badge-pending">
        Pending Verification
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]" data-testid="badge-unverified">
      Unverified
    </Badge>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const AnswerCard = ({ row }) => {
  const c = row.candidate;
  return (
    <Card className="overflow-hidden" data-testid={`qa-candidate-${c.slug}`}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-3">
          {c.photo ? (
            <img src={c.photo} alt={c.name} className="h-11 w-11 rounded-full object-cover border border-slate-200" />
          ) : (
            <div className="h-11 w-11 rounded-full bg-secondary border border-slate-200 flex items-center justify-center font-heading font-bold text-primary">
              {(c.name || "?").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-heading font-semibold text-foreground leading-tight" data-testid="qa-candidate-name">{c.name}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <VerificationBadge status={c.verification_status} />
              {c.campaign_website && (
                <a href={c.campaign_website} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] text-primary underline underline-offset-2 inline-flex items-center gap-0.5"
                   data-testid="qa-candidate-website">
                  Campaign website <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {row.status === "answered" && (
          <div className="mt-4">
            <p className="text-sm text-foreground whitespace-pre-wrap leading-6" data-testid="qa-answer-text">{row.answer}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground">
              <span data-testid="qa-answer-submitted">Submitted {fmtDate(row.submitted_at)}</span>
              {row.was_updated && (
                <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[10px]" data-testid="qa-answer-updated">
                  Updated {fmtDate(row.updated_at)}
                </Badge>
              )}
              {row.source_url && (
                <a href={row.source_url} target="_blank" rel="noopener noreferrer"
                   className="text-primary underline underline-offset-2 inline-flex items-center gap-0.5" data-testid="qa-answer-source">
                  Candidate's cited source <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        )}

        {row.status === "no_response" && (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-secondary/60 p-4" data-testid="qa-no-response">
            <p className="text-sm font-semibold text-foreground">Invited — No Response Yet</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              This candidate was invited on {fmtDate(row.invited_at)} and has not submitted an answer.
            </p>
          </div>
        )}

        {row.status === "withheld" && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="qa-withheld">
            <p className="text-xs text-amber-900">{row.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AskTheCandidates() {
  const [params, setParams] = useSearchParams();
  const [questions, setQuestions] = useState(null);
  const [selected, setSelected] = useState(params.get("q") || "");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get("/public/qa/questions").then((r) => {
      setQuestions(r.data);
      if (!selected && r.data.length) setSelected(r.data[0].id);
    }).catch(() => setQuestions([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return;
    setLoadingDetail(true);
    setParams({ q: selected }, { replace: true });
    api.get(`/public/qa/questions/${selected}`)
      .then((r) => setDetail(r.data)).catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-4xl">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">Candidate Q&A Corner</Badge>
        <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300 text-[10px]"
               data-testid="qa-beta-label">WitnessYourVote BETA</Badge>
      </div>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="qa-page-title">
        Ask the Candidates
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        One community question. Every candidate gets the identical question, the same character limit,
        the same deadline and the same display treatment — and every response (or non-response) is shown.
      </p>

      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3.5" data-testid="qa-non-endorsement">
        <p className="text-xs text-blue-900 inline-flex items-start gap-2">
          <Scale className="h-4 w-4 shrink-0 mt-0.5" />
          Candidate answers are provided by the candidates themselves and are not endorsements by WitnessYourVote.
        </p>
      </div>

      {questions === null && <Skeleton className="h-64 mt-6" />}

      {questions !== null && questions.length === 0 && (
        <Card className="mt-6" data-testid="qa-empty">
          <CardContent className="p-8 text-center">
            <CircleHelp className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading font-semibold text-foreground">No candidate questions published yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              When a community question opens, every invited candidate and their answer status will appear here.
            </p>
            <Button asChild size="sm" variant="outline" className="mt-4">
              <Link to="/community-voice">Explore Community Voice</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {questions !== null && questions.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="qa-question-tabs">
          {questions.map((q) => (
            <Button key={q.id} size="sm" variant={q.id === selected ? "default" : "outline"}
                    className={q.id === selected ? "bg-primary hover:bg-primary/90" : ""}
                    onClick={() => setSelected(q.id)} data-testid={`qa-question-tab-${q.id}`}>
              {q.question.length > 48 ? q.question.slice(0, 48) + "…" : q.question}
            </Button>
          ))}
        </div>
      )}

      {loadingDetail && <Skeleton className="h-80 mt-6" />}

      {detail && !loadingDetail && (
        <div className="mt-6">
          <Card className="border-t-4 border-t-primary" data-testid="qa-question-card">
            <CardContent className="p-6 sm:p-7">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
                <MessageSquareQuote className="h-3.5 w-3.5 text-accent" /> Community Question
              </p>
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-foreground mt-1" data-testid="qa-question-text">
                {detail.question.question}
              </h2>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px] gap-1">
                  <CalendarDays className="h-3 w-3" /> Deadline: {fmtDate(detail.question.deadline)}
                </Badge>
                <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px] gap-1">
                  <Clock3 className="h-3 w-3" /> {detail.question.status === "open" ? "Open for answers" : "Closed"}
                </Badge>
                <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]" data-testid="qa-limit-badge">
                  {detail.question.char_limit}-character limit — identical for every candidate
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-3" data-testid="qa-rules">{detail.rules}</p>
              <p className="text-xs font-semibold text-foreground mt-2" data-testid="qa-counts">
                {detail.answered_count} of {detail.invited_count} invited candidate{detail.invited_count === 1 ? "" : "s"} answered
              </p>
            </CardContent>
          </Card>

          <div className="space-y-4 mt-5" data-testid="qa-responses">
            {detail.responses.map((row) => <AnswerCard key={row.candidate.id} row={row} />)}
          </div>

          <p className="text-[11px] text-muted-foreground italic mt-5" data-testid="qa-footer-disclaimer">
            {detail.disclaimer} Candidate Q&A is completely separate from voter declarations, witness records
            and individual Community Poll responses — candidates never see any private voter information.
          </p>
          <div className="mt-3">
            <ReportErrorButton context="Ask the Candidates" defaultCategory="incorrect_candidate_info" />
          </div>
        </div>
      )}
    </div>
  );
}
